import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, IconButton } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  addSourceInfoInCalculationFlow,
  deleteSourceFromFlow,
  moveSourceCard,
  setAggregationForFlow,
  setParameterForFlow,
  updateParameterCardPosition,
} from '../../../redux/Calculations/CalculationsActions';
import { Menu, MenuItem, Popover } from '@mui/material';
import SourceListCard from './SourceListCard';
import Conditions from '../Conditions/Conditions';
import SourceListNumberCard from './SourceListNumberCard';
import BaseValue from './log/BaseValue';
import ReplaceSettings from './replace/ReplaceSettings';
import SplitSettings from './split/SplitSettings';
import SubstringLeftSettings from './substring/SubstringLeftSettings';
import SubstringRightSettings from './substring/SubstringRightSettings';
import { minMax } from '../constants';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import AddDateInterval from '../DateControls/AddDateInterval';
import NoFields from '../DateControls/NoFields';
import MinMaxDate from '../DateControls/MinMaxDate';
import TruncateDate from '../DateControls/TruncateDate';
import DateToNameOrNumberPipe from '../DateControls/DateToNameOrNumberPipe';
import DateDifference from '../DateControls/DateDifference';
import StringToDate from '../DateControls/StringToDate';
import { getFlowFieldNameIfExists } from '../utils';
import CalculationAggregation from './CalculationAggregation';
import FlowDetails from './FlowDetails';
import StringNumberDropZone from '../DropZones/StringNumberDropZone';
import Summarize from '../Aggregations/Summarize';
import { fontSize } from '../../..';
import SelectParameter from '../../ChartFieldFilter/SelectParameter';
import SelectedParameter from './SelectedParameter';
import { useDispatch } from 'react-redux';
import { TypeConversion } from '../DataTypeConversion/TypeConversion';
import { RootState } from '../../../redux';
import { useSelector } from 'react-redux';
import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';
import { ICalculationFieldsMissingField } from '../../../redux/Calculations/CurrentCalculationSessionInterface';
import Power from './NumberControls/Power';

type SourceType = 'field' | 'flow' | 'text' | 'integer' | 'decimal' | 'binary' | 'date';

const CalculationRightPanel = () => {
  // selectors
  const { calculations, tabTileProps, chartProperties } = useSelector((state: RootState) => {
    return {
      calculations: state.calculations,
      tabTileProps: state.tabTileProps,
      chartProperties: state.chartProperties,
    };
  });

  const propKey = useMemo(
    () => `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`,
    [tabTileProps.selectedTabId, tabTileProps.selectedTileId],
  );
  const currentCalculationSession =
    calculations.properties[propKey]?.currentCalculationSession ?? null;
  const flows = currentCalculationSession?.calculationInfo?.flows ?? {};
  const activeFlow = currentCalculationSession?.activeFlow ?? null;

  // if (!currentCalculationSession || !activeFlow) return null;

  const activeFlowData = activeFlow ? (flows[activeFlow]?.[0] ?? null) : null;

  const isFlowsEmpty = !activeFlowData || Object.keys(flows).length === 0;

  const calculationFields = currentCalculationSession?.calculationInfo?.fields ?? {};

  const isIfElse = activeFlowData?.flow === 'IfElse';

  const sources = activeFlowData?.source ?? [];

  // Local state
  const [activeFieldNamesArray, setActiveFieldNamesArray] = useState<any[]>([]);
  const [activeFlowName, setActiveFlowName] = useState<string>('');
  const activeFlowType: string | null = currentCalculationSession?.activeFlowType ?? null;
  const activeCondition = isIfElse ? (currentCalculationSession?.activeCondition ?? null) : null;
  const dispatch = useDispatch();
  const [showBorder, setShowBorder] = useState(false);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);
  const [paramSource, setParamSource] = useState<any[]>([]);
  const [addParameter, setAddParameter] = useState(false);
  const [isAggregationActive, setIsAggregationActive] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [alert, setAlert] = useState<any>(null);
  const [anchorElForSource, setAnchorElForSource] = useState<HTMLButtonElement | null>(null);
  const [missingFields, setMissingFields] = useState<ICalculationFieldsMissingField[]>([]);

  // const isFlowParameterized =
  //   currentCalculationSession.flowParameters[activeFlow];

  // To update the parameter index in the calculation session according to the source index
  const updateParameterIndex = (hoverIndex: number, dragIndex: number, type: any) => {
    if (!activeFlowData || !activeFlow) return;
    const parameters = activeFlowData.parameter;

    // To check if the parameter is between the dragIndex and hoverIndex
    const checkIfParameterIsPresent = parameters?.filter(
      (param: any) =>
        (dragIndex <= param.index && hoverIndex >= param.index) ||
        (dragIndex >= param.index && hoverIndex <= param.index),
    );

    if (checkIfParameterIsPresent?.length === 0 && type.type !== 'parameter-card') {
      // console.log(
      //   'Means the parameter index is not present btw the card index and parameter index',
      // );
      return;
    }

    // switch the parameter index according to its position in source array to maintain the connection
    // if the parameter is present and the card is dragged to the bottom of parameter card
    const moveUp = checkIfParameterIsPresent?.filter(
      (param: any) => param.index >= hoverIndex && dragIndex <= param.index,
    );
    // if the parameter is present and the card is dragged to the top of parameter card
    const moveDown = checkIfParameterIsPresent?.filter(
      (param: any) => param.index <= hoverIndex && dragIndex >= param.index,
    );

    if (moveUp?.length > 0) {
      moveUp?.forEach((param: any) => {
        dispatch(
          updateParameterCardPosition(propKey, activeFlow, param.parameterId, param.index - 1),
        );
      });
    }
    if (moveDown.length > 0) {
      moveDown.forEach((param: any) => {
        dispatch(
          updateParameterCardPosition(propKey, activeFlow, param.parameterId, param.index + 1),
        );
      });
    }
  };

  const stringFunctions = [
    'lowercase',
    'uppercase',
    'propercase',
    'trim',
    'substringleft',
    'substringright',
    'length',
    'ltrim',
    'rtrim',
    'replace',
    'split',
  ];

  const isFlowUsedInOtherFlowsAsSource = (flowId: string) => {
    if (!currentCalculationSession) {
      return { isUsed: false, allFlowsWithFlowAsSource: [] as string[] };
    }
    const allFlowsWithFlowAsSource = Object.keys(
      currentCalculationSession.calculationInfo.flows,
    ).filter((flow: any) =>
      currentCalculationSession.calculationInfo.flows[flow][0].sourceType.includes('flow'),
    );
    const isUsedInFlows = allFlowsWithFlowAsSource.some((flow) =>
      currentCalculationSession.calculationInfo.flows[flow].some((f: any) =>
        f.source.includes(flowId),
      ),
    );

    const isUsedInFilters = Object.values(
      currentCalculationSession.calculationInfo.conditionFilters,
    ).some((filter: any) =>
      filter[0].conditions.some(
        (condition: any) =>
          condition.leftOperand[0] === flowId || condition.rightOperand.includes(flowId),
      ),
    );

    return { isUsed: isUsedInFlows || isUsedInFilters, allFlowsWithFlowAsSource };
  };

  // const doesSourceArrayContainFieldOrFlow = sourceTypes?.includes("field") || sourceTypes?.includes("flow")

  useEffect(() => {
    if (!activeFlowData) {
      setActiveFlowName('');
      return;
    }
    setActiveFlowName(activeFlowData.flow);
  }, [activeFlowData]);

  useEffect(() => {
    if (!activeFlowData) return;

    // TODO: [activeFlow][0] <-- here this 0 is step. later introduce a step variable into this point. Like some flows will have multiple steps for example an if else statement
    const sourceIdsArray = activeFlowData.source;
    const sourceTypeArray = activeFlowData.sourceType;

    const activeFieldNamesArray = sourceIdsArray.map((sourceId: string, index: number) => {
      return {
        ...calculationFields[sourceId],
        sourceUID: sourceId,
        sourceDataType: sourceTypeArray[index],
      };
    });

    setActiveFieldNamesArray(activeFieldNamesArray);

    // TODO: activeFlow[0] <-- may arise a need to change 0 to dynamic value
  }, [activeFlowData, calculationFields]);

  useEffect(() => {
    if (!activeFlowData || !activeFlow) return;

    const activeFlowName = activeFlowData.flow;

    const flowFieldName = getFlowFieldNameIfExists(activeFlowName);

    setFlowName(flowFieldName ? flowFieldName : activeFlowName);

    setAddParameter(false); // reset the addParameter state if not parameter is selected
  }, [activeFlow]);

  useEffect(() => {
    if (!activeFlowData || !activeFlow) return;

    setIsAggregationActive(activeFlowData.isAggregation);

    dispatch(setAggregationForFlow(activeFlow, activeFlowData.isAggregation, propKey));

    // if parameter is present and flowParameters is false then set flowParameters to true
    const isParameterPresent = activeFlowData.parameter.length > 0;
    dispatch(setParameterForFlow(propKey, activeFlow, isParameterPresent));

    if (activeFlowData.flow === 'IfElse') return;

    setParamSource(
      activeFlowData.parameter.map((param: any) => {
        return {
          id: param.parameterId,
        };
      }),
    );
  }, [activeFlow, activeFlowData]);

  const handleSourceCardCrossButton = (sourceId: string, sourceIndex: number) => {
    if (!activeFlowData || !activeFlow) return;
    // console.log(sourceId, sourceIndex);

    const checkIfParameterIsPresent = activeFlowData.parameter.filter(
      (param: any) => param.index > sourceIndex,
    );

    if (checkIfParameterIsPresent.length > 0) {
      checkIfParameterIsPresent.forEach((param: any) => {
        dispatch(
          updateParameterCardPosition(propKey, activeFlow, param.parameterId, param.index - 1),
        );
      });
    }

    dispatch(deleteSourceFromFlow(activeFlow, sourceId, propKey, sourceIndex));
  };

  const moveCard = (dragIndex: number, hoverIndex: number) => {
    if (!activeFlow || !activeCondition) return;
    // introduce step
    dispatch(moveSourceCard(dragIndex, hoverIndex, propKey, activeFlow, activeCondition));
  };

  const sourceTypeList = useMemo(() => ['text', 'decimal', 'integer', 'binary', 'date'], []);

  const handleClickForSource = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForSource(event.currentTarget);
  };

  const open = Boolean(anchorElForSource);
  const id = open ? 'simple-popover' : undefined;

  const handleCloseForSource = () => {
    setAnchorElForSource(null);
    setSubMenuAnchorEl(null);
  };

  const getsourceCount = () => {
    if (!activeFlowData || !activeFlow) return 0;
    const sourceTypes = activeFlowData.sourceType;
    const srcs = activeFlowData.source;
    const flow = activeFlowData.flow;
    if (
      [
        'addition',
        'subtraction',
        'multiplication',
        'max',
        'min',
        'ceiling',
        'division',
        'floor',
        'absolute',
      ].includes(flow)
    )
      return srcs?.length ?? 0;
    if (flow === 'log') return srcs.length >= 2 ? 1 : 0;
    // string type formulas

    if (['lowercase', 'uppercase', 'propercase', 'trim', 'length', 'ltrim', 'rtrim'].includes(flow))
      return srcs.length >= 1 ? 1 : 0;
    if (['substringleft', 'substringright'].includes(flow)) return srcs.length >= 3 ? 1 : 0;
    if (flow === 'concat') return srcs.length ?? 0;
    if (flow === 'replace') return srcs.length >= 3 ? 1 : 0;
    if (flow === 'split') return srcs.length >= 4 ? 1 : 0;

    //aggregation type formulas
    if (flow === 'summarize') return ['field', 'flow'].includes(sourceTypes[0]) ? 1 : 0;
    //type conversion formulas
    if (flow === 'typeCast') return ['field', 'flow'].includes(sourceTypes[0]) ? 1 : 0;
    return 0;
  };

  // Handles opening a sub-menu
  const handleSubMenuClick = (event: any) => {
    setSubMenuAnchorEl(event.currentTarget);
  };

  // Handles closing the sub-menu
  const handleCloseSubMenu = () => {
    setSubMenuAnchorEl(null);
  };

  const getMissingFields = async () => {
    if (!activeFlowName || !activeFlow) return;
    const selectedDs = chartProperties.properties[propKey]?.selectedDs;
    if (!selectedDs) return;

    const table = tabTileProps.tablesForSelectedDataSets[selectedDs.id];
    const promises = flows[activeFlow].flatMap((flow) =>
      flow.source.map(async (s: string, index: number) => {
        const field = calculationFields[s];
        if (!field) return null;

        const res = await checkIfMissingColumn(field, selectedDs, table);

        if (!res.isMissing) return null;

        return {
          [s]: { ...field, index },
        };
      }),
    );
    const results = await Promise.all(promises);

    const missingFields = results.filter(
      (item): item is ICalculationFieldsMissingField => item !== null,
    );

    setMissingFields(missingFields);
  };

  useEffect(() => {
    getMissingFields();
  }, [activeFlowName, calculationFields]);

  if (!activeFlowData || !activeFlowType || !currentCalculationSession) {
    return null;
  }

  return (
    <>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      {!isFlowsEmpty && activeFlow !== null && activeFlow !== undefined ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            boxSizing: 'border-box',
            paddingLeft: 0.5,
            paddingRight: 0.5,
          }}
        >
          <CalculationAggregation
            activeFlowName={activeFlowName}
            selectedAggregation={
              activeFlowData.isAggregation ? 'Visual level aggregation' : 'Row level calculation'
            }
            slectedLabel={isAggregationActive ? 'visual' : 'row'}
            propKey={propKey}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '442px',
              overflowY: 'hidden',
              overflowX: 'hidden',
              boxSizing: 'border-box',
              scrollbarGutter: 'stable both-edges',
              '&:hover': {
                overflowY: 'auto',
              },
              '&::-webkit-scrollbar': {
                width: '5px',
              },
            }}
          >
            {activeFlowName === 'IfElse' ? (
              <Conditions key={activeFlowName} propKey={propKey} />
            ) : activeFlowName === 'addDateInterval' ? (
              <AddDateInterval
                propKey={propKey}
                // lengthOfSource={
                //   activeFlowData.source
                //     .length as number
                // }
                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
              />
            ) : ['currentTimestamp', 'currentDate'].includes(activeFlowName) ? (
              <NoFields flowName={activeFlowName === 'currentDate' ? 'Today' : 'Now'} />
            ) : activeFlowName === 'truncateDate' ? (
              <TruncateDate
                propKey={propKey}
                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
              />
            ) : ['datePartName', 'datePartNumber'].includes(activeFlowName) ? (
              <DateToNameOrNumberPipe
                propKey={propKey}
                lengthOfSource={1}
                flow={activeFlowName}
                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
              />
            ) : ['minDate', 'maxDate'].includes(activeFlowName) ? (
              <MinMaxDate
                flowName={activeFlowName}
                propKey={propKey}
                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
              />
            ) : activeFlowName === 'dateInterval' ? (
              <DateDifference
                propKey={propKey}
                flow={activeFlowName}
                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
              />
            ) : activeFlowName === 'stringToDate' ? (
              <StringToDate
                propKey={propKey}
                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
              />
            ) : activeFlowName === 'power' ? (
              <Power
                propKey={propKey}
                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    fontWeight: 'bold',
                    textAlign: 'start',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <section style={{ display: 'flex', gap: '5px' }}>
                    <FlowDetails
                      flowName={
                        flowName === 'typeCast'
                          ? 'Type Conversion'
                          : flowName.replace(/\b\w/g, (char) => char.toUpperCase())
                      }
                      currentValue={getsourceCount()}
                      min={minMax[activeFlowType][activeFlowName]?.min}
                      max={minMax[activeFlowType][activeFlowName]?.max}
                    />
                  </section>

                  {activeFlowName !== 'log' &&
                    (activeFlowType !== 'String' || activeFlowName === 'concat') && (
                      <IconButton
                        size="small"
                        onClick={handleClickForSource}
                        disabled={
                          isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed ||
                          getsourceCount() === minMax[activeFlowType][activeFlowName]?.max
                        }
                        sx={{
                          display:
                            flowName === 'Summarize' || flowName === 'Type Conversion'
                              ? 'none'
                              : 'block',
                        }}
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    )}
                  {activeFlowName === 'log' && (
                    <IconButton
                      size="small"
                      disabled={
                        isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed ||
                        currentCalculationSession?.calculationInfo.flows[activeFlow][
                          activeCondition === null ? 0 : activeCondition
                        ].sourceType.length === 2
                      }
                      onClick={handleClickForSource}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  )}
                  <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorElForSource}
                    onClose={handleCloseForSource}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                  >
                    <Menu
                      id="lock-menu"
                      anchorEl={anchorElForSource}
                      open={open}
                      onClose={handleCloseForSource}
                      MenuListProps={{
                        'aria-labelledby': 'lock-button',
                        role: 'listbox',
                      }}
                      sx={{
                        '& .MuiMenu-paper': {
                          boxShadow: '0px 2px 8px rgba(0,0,0,0.4)',
                        },
                      }}
                    >
                      <MenuItem
                        onClick={handleSubMenuClick}
                        sx={{
                          width: '120px',
                          padding: '3px 15px',
                          fontSize: fontSize.small,
                          backgroundColor: subMenuAnchorEl ? '#f4f4f4' : 'transparent',
                          '&:hover': {
                            backgroundColor: '#40404',
                          },
                        }}
                      >
                        Static values
                      </MenuItem>
                      <MenuItem
                        sx={{
                          width: '120px',
                          padding: '3px 15px',
                          fontSize: fontSize.small,
                          '&:hover': {
                            backgroundColor: '#40404',
                          },
                        }}
                        disabled={activeFlowName === 'concat'}
                        onClick={() => {
                          setAddParameter(true);
                          handleCloseForSource();
                        }}
                      >
                        Parameterized
                      </MenuItem>

                      <Menu
                        open={Boolean(subMenuAnchorEl)}
                        anchorEl={subMenuAnchorEl}
                        onClose={handleCloseSubMenu}
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: -70,
                        }}
                        sx={{
                          '& .MuiMenu-paper': {
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                          },
                        }}
                        MenuListProps={{
                          sx: {
                            '& .MuiMenuItem-root': {
                              py: 0,
                              fontSize: fontSize.extraSmall,
                            },
                          },
                        }}
                      >
                        {sourceTypeList.map((item: any, index: number) => {
                          return (
                            <MenuItem
                              disabled={
                                (activeFlowType === 'Number' &&
                                  (item === 'binary' || item === 'text' || item === 'date')) ||
                                (activeFlowType === 'String' &&
                                  (item === 'binary' ||
                                    item === 'integer' ||
                                    item === 'decimal' ||
                                    item === 'date'))
                              }
                              dense
                              data-value={item}
                              key={index}
                              onClick={(event) => {
                                // TODO: add validation for other source types too
                                if (
                                  event.currentTarget.dataset.value !== 'decimal' &&
                                  event.currentTarget.dataset.value !== 'integer' &&
                                  event.currentTarget.dataset.value !== 'binary' &&
                                  !(
                                    activeFlowType === 'String' &&
                                    activeFlowName === 'concat' &&
                                    event.currentTarget.dataset.value === 'text'
                                  )
                                )
                                  return;

                                if (
                                  activeFlowType === 'String' &&
                                  activeFlowName === 'concat' &&
                                  event.currentTarget.dataset.value === 'text'
                                ) {
                                  dispatch(
                                    addSourceInfoInCalculationFlow(
                                      '',
                                      {
                                        uid: activeFlow,
                                        flow: currentCalculationSession.calculationInfo.flows[
                                          activeFlow
                                        ][activeCondition === null ? 0 : activeCondition].flow,

                                        // TODO: introduce aggregation type here, like sum, avg, min, max etc
                                      },
                                      propKey,
                                      event.currentTarget.dataset.value,
                                      true,
                                      'sum',
                                      activeCondition === null ? 0 : activeCondition,
                                    ),
                                  );
                                } else {
                                  dispatch(
                                    addSourceInfoInCalculationFlow(
                                      String(0),
                                      {
                                        uid: activeFlow,
                                        flow: currentCalculationSession.calculationInfo.flows[
                                          activeFlow
                                        ][activeCondition === null ? 0 : activeCondition].flow,

                                        // TODO: introduce aggregation type here, like sum, avg, min, max etc
                                      },
                                      propKey,
                                      event.currentTarget.dataset.value,
                                      true,
                                      'sum',
                                      activeCondition === null ? 0 : activeCondition,
                                    ),
                                  );
                                }

                                setAnchorElForSource(null);
                              }}
                            >
                              {item}
                            </MenuItem>
                          );
                        })}
                      </Menu>
                    </Menu>
                  </Popover>
                </div>
                {activeFlowName === 'summarize' ? (
                  <Summarize
                    propKey={propKey}
                    isFlowUsedInOtherFlowsAsSource={
                      isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed
                    }
                  />
                ) : activeFlowName === 'typeCast' ? (
                  <TypeConversion propKey={propKey} />
                ) : (
                  <div
                    style={{
                      marginBottom: '2px',
                    }}
                  >
                    <div
                      style={{
                        minHeight: '50px',
                      }}
                    >
                      <StringNumberDropZone
                        activeFlowName={activeFlowName}
                        propKey={propKey}
                        activeFlowType={activeFlowType}
                        showBorder={showBorder}
                        disableDrop={
                          isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed &&
                          missingFields.length === 0
                        }
                        setShowBorder={setShowBorder}
                        missingFields={missingFields}
                        getMissingFields={getMissingFields}
                      >
                        <div>
                          {currentCalculationSession.calculationInfo.flows[activeFlow][
                            activeCondition === null ? 0 : activeCondition
                          ].source.length === 0 && (
                            <p
                              style={{
                                fontSize: '11px',
                                color: '#999999',
                                paddingLeft: '8px',
                                textAlign: 'start',
                              }}
                            >
                              {stringFunctions.includes(activeFlowName)
                                ? 'Drag the source from below column to here'
                                : !addParameter
                                  ? 'Drop a column or click + to add static values'
                                  : ''}
                            </p>
                          )}
                        </div>

                        {sources?.map((source: string, index: number) => {
                          if (source === undefined || source === null) return null;
                          const sourceType = currentCalculationSession?.calculationInfo?.flows[
                            activeFlow
                          ][0].sourceType[index] as SourceType | undefined;
                          const parameterSource = currentCalculationSession?.calculationInfo?.flows[
                            activeFlow
                          ][0].parameter.find((param: any) => param.index === index);

                          if (
                            sourceType &&
                            !['field', 'flow', 'text', 'integer', 'decimal'].includes(sourceType)
                          )
                            return null;

                          if (sourceType === 'field') {
                            return (
                              <SourceListCard
                                flows={currentCalculationSession.calculationInfo.flows}
                                fields={calculationFields}
                                cardType="field"
                                index={index}
                                value={source}
                                aggregationMethod={
                                  isAggregationActive
                                    ? (currentCalculationSession?.calculationInfo?.flows[
                                        activeFlow
                                      ][0].aggregation[index] ?? '')
                                    : ''
                                }
                                calculationFlowUID={activeFlow}
                                displayName={
                                  currentCalculationSession?.calculationInfo?.fields[source]
                                    ?.fieldName
                                }
                                handleSourceCardCrossButton={handleSourceCardCrossButton}
                                isAggregationActive={isAggregationActive}
                                moveCard={moveCard}
                                propKey={propKey}
                                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(
                                  activeFlow,
                                )}
                                updateParameterIndex={updateParameterIndex}
                                missingFieldsLength={missingFields.length}
                              />
                            );
                          } else if (sourceType === 'flow') {
                            return (
                              <SourceListCard
                                flows={currentCalculationSession.calculationInfo.flows}
                                cardType="flow"
                                index={index}
                                value={source}
                                aggregationMethod={
                                  isAggregationActive
                                    ? (activeFlowData.aggregation[index] ?? '')
                                    : ''
                                }
                                calculationFlowUID={activeFlow}
                                displayName={currentCalculationSession.flowDisplayNames[source]}
                                handleSourceCardCrossButton={handleSourceCardCrossButton}
                                isAggregationActive={isAggregationActive}
                                moveCard={moveCard}
                                propKey={propKey}
                                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(
                                  activeFlow,
                                )}
                                updateParameterIndex={updateParameterIndex}
                                missingFieldsLength={missingFields.length}
                              />
                            );
                          } else if (
                            (sources.length === 1 || (sources.length === 2 && index === 1)) &&
                            activeFlowName === 'log'
                          ) {
                            if (parameterSource) {
                              return (
                                <SelectedParameter
                                  index={index}
                                  value={source}
                                  propKey={propKey}
                                  paramSource={paramSource.find(
                                    (param: any) => param.id === parameterSource.parameterId,
                                  )}
                                  calculationFlowUID={activeFlow}
                                  moveCard={moveCard}
                                  handleSourceCardCrossButton={handleSourceCardCrossButton}
                                  updateParameterIndex={updateParameterIndex}
                                />
                              );
                            }
                            return null;
                          } else if (activeFlowName === 'split' && index !== 0) {
                            return null;
                          } else if (activeFlowName === 'substringleft') return null;
                          else if (activeFlowName === 'substringright') return null;

                          // check if the source is a parameter and if it is not then render the source card
                          // because parameter sourceType are also decimal and integer
                          if (
                            (sourceType === 'decimal' ||
                              sourceType === 'integer' ||
                              (activeFlowName === 'concat' && sourceType === 'text')) &&
                            !parameterSource
                          )
                            return (
                              <SourceListNumberCard
                                fields={calculationFields}
                                index={index}
                                value={source}
                                valueType={sourceType}
                                handleSourceCardCrossButton={handleSourceCardCrossButton}
                                moveCard={moveCard}
                                isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(
                                  activeFlow,
                                )}
                                updateParameterIndex={updateParameterIndex}
                                missingFieldsLength={missingFields.length}
                                propKey={propKey}
                              />
                            );

                          if (parameterSource) {
                            return (
                              <SelectedParameter
                                propKey={propKey}
                                index={index}
                                value={source}
                                paramSource={paramSource.find(
                                  (param: any) => param.id === parameterSource.parameterId,
                                )}
                                calculationFlowUID={activeFlow}
                                moveCard={moveCard}
                                handleSourceCardCrossButton={handleSourceCardCrossButton}
                                updateParameterIndex={updateParameterIndex}
                              />
                            );
                          }

                          return null;
                        })}
                        {addParameter && (
                          <SelectParameter
                            propKey={propKey}
                            type="calculation"
                            calculationSession={currentCalculationSession}
                            setSource={setParamSource}
                            addParameter={addParameter}
                            setAddParameter={setAddParameter}
                          />
                        )}
                      </StringNumberDropZone>
                    </div>

                    {activeFlowName === 'log' && <BaseValue />}
                    {activeFlowName === 'replace' && <ReplaceSettings />}
                    {activeFlowName === 'split' && <SplitSettings />}
                    {activeFlowName === 'substringleft' && <SubstringLeftSettings />}
                    {activeFlowName === 'substringright' && <SubstringRightSettings />}
                  </div>
                )}
              </div>
            )}
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
};

export default CalculationRightPanel;
